/**
 * Journey-map slug derivation.
 *
 * The backend column is `SlugField(max_length=255, unique=True, blank=True)` and the Django
 * model's `save()` would auto-generate a unique slug from the name — but the real runtime is the
 * Go generic-CRUD layer, which writes the row directly and never runs that Python `save()`. So an
 * empty `slug: ''` persists verbatim and collides on the unique constraint the moment a second
 * blank-slug map is created. The slug therefore has to be derived on the client, before the row is
 * sent, and made unique against the maps that already exist.
 */

/** Kebab-case a name into a slug, matching the backend SlugField length cap. */
export const slugifyJourneyMapName = (value: string): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 255);

/**
 * Return `base` if free, otherwise the first `base-2`, `base-3`, … that is not in `taken`.
 * `taken` should already exclude the current map's own slug so re-saving a map keeps it.
 * Suffixing never pushes the slug past the 255-char column limit.
 */
export const ensureUniqueJourneyMapSlug = (
  base: string,
  taken: Set<string>,
): string => {
  const normalizedBase = (base || '').toLowerCase();
  if (!normalizedBase) return normalizedBase;
  if (!taken.has(normalizedBase)) return normalizedBase;

  for (let idx = 2; ; idx += 1) {
    const suffix = `-${idx}`;
    const candidate = `${normalizedBase.slice(0, 255 - suffix.length)}${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }
};
